import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { trackLandingEvent } from '@/lib/landingAnalytics';

const PAGE_SIZE = 4; // 2x2 per page

export function MobileTitaniumAgencies() {
  const navigate = useNavigate();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);

  const { data: agencies } = useQuery({
    queryKey: ['mobile-titanium-agencies-subscribed'],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      // Only agencies with an ACTIVE Titanium subscription (super_hot or hot tier)
      const { data: feats } = await supabase
        .from('featured_listings')
        .select('listing_id, tier, priority')
        .eq('listing_type', 'agency')
        .in('tier', ['super_hot', 'hot'])
        .eq('is_active', true)
        .lte('start_date', today)
        .gte('end_date', today);
      const ids = Array.from(new Set((feats || []).map((f: any) => f.listing_id)));
      if (!ids.length) return [];

      const { data: ags } = await supabase
        .from('agencies')
        .select('id, name, logo_url, city, slug')
        .in('id', ids)
        .eq('is_verified', true);
      const list = ags || [];
      const rankMap: Record<string, number> = {};
      (feats || []).forEach((f: any) => {
        const r = f.tier === 'super_hot' ? 2 : 1;
        rankMap[f.listing_id] = Math.max(rankMap[f.listing_id] || 0, r);
      });
      return list.sort((a, b) => (rankMap[b.id] || 0) - (rankMap[a.id] || 0));
    },
  });

  const pages = useMemo(() => {
    if (!agencies?.length) return [] as any[][];
    const out: any[][] = [];
    for (let i = 0; i < agencies.length; i += PAGE_SIZE) out.push(agencies.slice(i, i + PAGE_SIZE));
    return out;
  }, [agencies]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth;
      if (!w) return;
      setPage(Math.round(el.scrollLeft / w));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [pages.length]);

  if (!agencies?.length) return null;

  const goToPage = (i: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };
  const prev = () => goToPage(Math.max(0, page - 1));
  const next = () => goToPage(Math.min(pages.length - 1, page + 1));

  const handleAgencyClick = (a: any, position: number) => {
    trackLandingEvent({
      event_type: 'titanium_agency_click',
      entity_type: 'agency',
      entity_id: a.id,
      metadata: { name: a.name, position, surface: 'mobile_homepage' },
    });
    navigate(`/agencies/${a.slug || a.id}`);
  };

  return (
    <section className="container py-6 md:hidden">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-bold">Titanium Agencies</h2>
        <div className="flex items-center gap-2">
          {pages.length > 1 && (
            <>
              <button
                onClick={prev}
                disabled={page === 0}
                aria-label="Previous agencies"
                className="h-7 w-7 rounded-full border bg-card flex items-center justify-center disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4 text-primary" />
              </button>
              <button
                onClick={next}
                disabled={page >= pages.length - 1}
                aria-label="Next agencies"
                className="h-7 w-7 rounded-full border bg-card flex items-center justify-center disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4 text-primary" />
              </button>
            </>
          )}
          <button onClick={() => navigate('/agencies')} className="text-xs font-semibold text-primary">View All</button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-hide -mx-3 px-3"
      >
        {pages.map((group, gi) => (
          <div key={gi} className="snap-start shrink-0 w-full grid grid-cols-2 gap-3 pr-3">
            {group.map((a: any, ai: number) => (
              <button
                key={a.id}
                onClick={() => handleAgencyClick(a, gi * PAGE_SIZE + ai)}
                className="flex items-start gap-2 p-2 rounded-lg border bg-card hover:shadow-md transition text-left"
              >
                <div className="h-14 w-14 shrink-0 rounded-md bg-muted flex items-center justify-center overflow-hidden">
                  {a.logo_url ? (
                    <img
                      src={optimizedImageUrl(a.logo_url, { width: 112, height: 112 })}
                      alt={a.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm leading-tight line-clamp-2">{a.name}</div>
                  <div className="flex items-center gap-1 text-[11px] text-primary mt-1.5">
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span className="truncate">{a.city || 'Ethiopia'}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>

      {pages.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-3">
          {pages.map((_, i) => (
            <button
              key={i}
              onClick={() => goToPage(i)}
              aria-label={`Go to page ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === page ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30'}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
